import type { Attachment } from "src/components/email/AttachmentManager";
import {
  type DetachedWindowPrewarmOptions,
  openDetachedWindow,
  prewarmDetachedWindow,
} from "src/lib/runtime/detachedWindows";
import { isTauriRuntime } from "src/lib/runtime/environment";

export const EMAIL_COMPOSE_SURFACE = "email-compose";

/**
 * A compose intent, flattened for transport.
 *
 * Deliberately a subset of `OpenComposeInput` rather than the thing itself: a launch payload is
 * JSON in localStorage, so it can carry the pre-fill but not a hydrated `Communication` entity or
 * an `onSent` closure. The window rebuilds what it needs from the ids.
 */
export interface EmailComposeWindowPayload {
  applicationId?: string;
  applicationEid?: string;
  applicantNames?: string;
  to?: string[];
  cc?: string[];
  subject?: string;
  body?: string;
  /** Fetched by the composer itself, which is what turns this into a threaded reply. */
  replyToCommunicationId?: string;
  initialAttachments?: Attachment[];
  /** Resolved by the window after a successful send. */
  proposedCommunicationId?: string;
  proposedCommunicationCreatedAt?: string;
}

const EMAIL_COMPOSE_WINDOW_OPTIONS: DetachedWindowPrewarmOptions = {
  surface: EMAIL_COMPOSE_SURFACE,
  title: "Compose Email",
  width: 780,
  height: 820,
  minWidth: 560,
  minHeight: 520,
  // No `beside-main`, unlike the chat window: opening a deal auto-opens both, and they would
  // otherwise be placed into the same slot beside the main window, one on top of the other.
};

function emailComposeInstanceKey(payload: EmailComposeWindowPayload): string {
  if (payload.proposedCommunicationId) {
    return `proposed-communication:${payload.proposedCommunicationId}`;
  }
  if (payload.replyToCommunicationId) {
    return `communication-reply:${payload.replyToCommunicationId}`;
  }
  return `application:${payload.applicationId ?? "none"}:compose`;
}

export function prewarmEmailComposeWindow(): Promise<void> {
  return prewarmDetachedWindow(EMAIL_COMPOSE_WINDOW_OPTIONS);
}

/**
 * Open (or focus) the native compose window for this intent.
 *
 * Keyed by what the draft is ABOUT rather than by a fresh id per call, so the auto-open path
 * cannot stack duplicate windows for one proposal across remounts or a second visit to the deal.
 */
/**
 * Hand a compose to its own window, or report that there is no window to hand it to.
 *
 * The synchronous half of `openDetachedEmailComposeWindow`, for callers that need to know whether
 * to fall back to the in-page composer — the same shape as `openPreviewWindow`.
 */
export function openEmailComposeWindow(payload: EmailComposeWindowPayload): boolean {
  if (!isTauriRuntime()) return false;
  void openDetachedEmailComposeWindow(payload).catch((error) =>
    console.warn("Could not open the compose window.", error)
  );
  return true;
}

export async function openDetachedEmailComposeWindow(
  payload: EmailComposeWindowPayload,
  { focus = true }: { focus?: boolean } = {}
): Promise<string> {
  const label = payload.applicantNames || payload.applicationEid;
  return openDetachedWindow({
    ...EMAIL_COMPOSE_WINDOW_OPTIONS,
    instanceKey: emailComposeInstanceKey(payload),
    payload,
    title: label ? `Compose — ${label}` : "Compose Email",
    focus,
  });
}
